<template>
  <div class="home">
    <div class="header">
      <div class="city-select" @click="showCitySelect = true">
        {{ currentCity }} <van-icon name="arrow-down" />
      </div>
      <div class="search-box">
        <van-search v-model="searchValue" placeholder="搜索门店/地点" shape="round" @input="onSearch" />
      </div>
    </div>

    <!-- 店铺列表 -->
    <div class="shop-list">
      <van-list v-model="loading" :finished="true" finished-text="没有更多了">
        <div class="shop-item" v-for="shop in shopList" :key="shop.shopId" @click="getShopMenu(shop)">
          <div class="shop-card">
            <div class="shop-header">
              <div class="shop-title">
                <van-icon name="star" />
                <span class="name">{{ shop.shopName }}</span>
              </div>
              <span class="distance">{{ shop.distanceDisplay }}</span>
            </div>
            <div class="shop-tags">
              <span class="tag hall">堂食</span>
            </div>
            <div class="shop-info">
              <p class="address">{{ shop.address }}</p>
              <p class="business-time">营业时间 {{ shop.businessHours }}</p>
            </div>
          </div>
        </div>
      </van-list>
    </div>

    <van-popup v-model="showCitySelect" position="right" :style="{ height: '100%', width: '100%' }">
      <div class="city-select-page">
        <van-nav-bar title="选择城市" left-arrow @click-left="showCitySelect = false" />
        <van-index-bar :index-list="indexList" class="city-list-component">
          <div v-for="(cities, letter) in groupedCities" :key="letter">
            <van-index-anchor :index="letter" />
            <van-cell v-for="city in cities" :key="city.code" :title="city.city" @click="selectCity(city.city)" />
          </div>
        </van-index-bar>
      </div>
    </van-popup>
  </div>
</template>

<script>
import areaData from "@/assets/area.json";
import request from "@/utils/request";

export default {
  name: "HomePage",
  created() {
    // 初始化城市列表
    this.initCityList();
    this.searchShops();
  },
  data() {
    // 生成索引列表
    const indexList = [
      ...new Set(areaData.cityList.map((c) => c.initial).filter(Boolean)),
    ].sort();

    return {
      currentCity: "广州市",
      currentCityInfo: this.getCityInfo("广州市"), // 默认广州市的信息
      searchValue: "",
      searchTimer: null, // 添加防抖定时器
      showCitySelect: false,
      indexList: indexList, // 从所有城市生成索引列表
      groupedCities: {}, // 将由initCityList生成
      shopList: [],
      loading: false,
      currentShop: null, // 添加当前选中的店铺
      cachedShopList: [], // 缓存的完整店铺列表
      cachedCity: "", // 缓存对应的城市
    };
  },
  mounted() {
    // 页面加载时请求获取用户定位
    this.getUserLocation();
  },
  methods: {
    // 获取用户地理位置
    getUserLocation() {
      this.$dialog
        .confirm({
          title: "获取定位",
          message: "需要获取您的位置信息以显示附近的门店",
          confirmButtonText: "允许",
          cancelButtonText: "取消",
        })
        .then(() => {
          // 用户同意，获取位置
          if (navigator.geolocation) {
            this.$toast.loading({
              message: "定位中...",
              forbidClick: true,
              duration: 0,
            });

            navigator.geolocation.getCurrentPosition(
              (position) => {
                this.$toast.clear();
                const { latitude, longitude } = position.coords;
                console.log("获取到用户位置：", { latitude, longitude });

                // 使用实际定位的经纬度更新当前城市信息
                this.currentCityInfo = {
                  ...this.currentCityInfo,
                  lat: latitude,
                  lng: longitude,
                };

                this.$toast.success("定位成功");
                // 定位成功后重新搜索店铺
                this.searchShops();
              },
              (error) => {
                this.$toast.clear();
                console.error("定位失败：", error);
                let errorMsg = "定位失败";
                switch (error.code) {
                  case error.PERMISSION_DENIED:
                    errorMsg = "您拒绝了定位请求";
                    break;
                  case error.POSITION_UNAVAILABLE:
                    errorMsg = "位置信息不可用";
                    break;
                  case error.TIMEOUT:
                    errorMsg = "定位请求超时";
                    break;
                }
                this.$toast.fail(errorMsg);
                // 定位失败，使用默认城市搜索店铺
                this.searchShops();
              },
              {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
              }
            );
          } else {
            this.$toast.fail("您的浏览器不支持定位功能");
            // 浏览器不支持，使用默认城市搜索店铺
            this.searchShops();
          }
        })
        .catch(() => {
          // 用户拒绝
          this.$toast("已取消定位");
          // 用户拒绝定位，使用默认城市搜索店铺
          this.searchShops();
        });
    },

    getCityInfo(cityName) {
      // 处理城市名称，统一加上"市"后缀（如果没有的话）
      const normalizedCityName = cityName.endsWith("市")
        ? cityName
        : `${cityName}市`;
      const cityInfo = areaData.cityList.find(
        (city) => city.city === normalizedCityName || city.city === cityName
      );
      if (cityInfo) {
        return {
          code: cityInfo.code,
          lng: cityInfo.lng || 113.24427795410156, // 如果没有经纬度，使用默认值
          lat: cityInfo.lat || 23.1258602142334,
        };
      }
      // 默认广州市的信息
      return {
        code: "440100",
        lng: 113.24427795410156,
        lat: 23.1258602142334,
      };
    },
    selectCity(city) {
      const cityName = typeof city === "string" ? city : city.city;
      this.currentCity = cityName;
      this.currentCityInfo = this.getCityInfo(cityName);
      console.log("选择城市：", cityName, "城市信息：", this.currentCityInfo);
      this.showCitySelect = false;
      // 切换城市时清空缓存
      this.cachedShopList = [];
      this.cachedCity = "";
      // 切换城市后重新搜索店铺
      this.searchShops();
    },
    groupCitiesByInitial(cityList, indexList) {
      const grouped = {};
      indexList.forEach((letter) => {
        grouped[letter] = cityList.filter((city) => city.initial === letter);
      });
      return grouped;
    },
    onSearch() {
      // 使用防抖，避免频繁请求
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.searchShops();
      }, 300);
    },
    // 初始化城市列表
    initCityList() {
      this.groupedCities = this.groupCitiesByInitial(
        areaData.cityList,
        this.indexList
      );
    },
    async searchShops() {
      try {
        this.loading = true;

        // 判断是否有搜索关键词
        const hasSearchName = this.searchValue.trim().length > 0;

        // 如果没有搜索关键词且有缓存且城市未变，直接使用缓存
        if (!hasSearchName && this.cachedCity === this.currentCity && this.cachedShopList.length > 0) {
          console.log("使用缓存的店铺列表，无需调用 API");
          this.shopList = this.cachedShopList;
          this.loading = false;
          return;
        }

        // 获取城市首字母作为 prefixChar
        const cityInfo = areaData.cityList.find(c => c.city === this.currentCity);
        const prefixChar = cityInfo ? cityInfo.initial : "B";

        // 从 store 中获取 promotionId
        const memberCoupon = this.$store.state.memberCoupon;
        const promotionId = memberCoupon ? memberCoupon.promotionId : undefined;

        let response;

        if (hasSearchName) {
          // 有搜索关键词，调用 /query-shops 接口
          const params = {
            channelCode: 20,
            brandId: 1,
            latitude: this.currentCityInfo.lat,
            longitude: this.currentCityInfo.lng,
            prefixChar: prefixChar,
            cityCode: this.currentCityInfo.code,
            cityName: this.currentCity,
            searchName: this.searchValue.trim(),
            promotionId: promotionId,
            pageNo: 1,
            pageSize: 20,
          };

          console.log("搜索店铺参数（按名称搜索）：", params);
          response = await request.post("/gm/query-shops", params);
        } else {
          // 无搜索关键词，调用 /list-shops-by-city 接口（初始加载或切换城市）
          const params = {
            channelCode: 20,
            brandId: 1,
            promotionId: promotionId,
            deliveryType: "SELF_PICK",
            unionId: "okmZaw96LkVkaPcfSIIBabbxc17E",
            latitude: this.currentCityInfo.lat,
            longitude: this.currentCityInfo.lng,
            nowLatitude: this.currentCityInfo.lat,
            nowLongitude: this.currentCityInfo.lng,
            prefixChar: prefixChar,
            cityCode: this.currentCityInfo.code,
            cityName: this.currentCity,
          };

          console.log("搜索店铺参数（按城市加载）：", params);
          response = await request.post("/gm/list-shops-by-city", params);
        }
        if (response) {
          response = response.data
        }

        console.log("response", response);
        console.log("店铺接口返回数据：", response);

        // 响应拦截器解包后，response = data 字段
        // 数据结构：response.shops.data.shopChannelSimplifyV2VOPager.data
        if (response && response.shops && response.shops.code == 0 && response.shops.data) {
          const shopData = response.shops.data.shopChannelSimplifyV2VOPager;
          if (shopData && shopData.data) {
            this.shopList = shopData.data;
            console.log("获取到的店铺列表：", this.shopList);
            
            // 如果是城市加载（无搜索关键词），缓存数据
            if (!hasSearchName) {
              this.cachedShopList = shopData.data;
              this.cachedCity = this.currentCity;
              console.log("已缓存店铺列表，城市：", this.cachedCity);
            }
          } else {
            console.error("shopChannelSimplifyV2VOPager 数据结构错误");
            this.$toast("店铺数据格式错误");
          }
        } else {
          const errorMsg = response && response.shops ? response.shops.msg : "接口返回数据格式错误";
          console.error("获取店铺列表失败：", errorMsg);
          this.$toast(errorMsg || "获取店铺列表失败");
        }
      } catch (error) {
        console.error("搜索店铺出错：", error);
      } finally {
        this.loading = false;
      }
    },
    formatBusinessTime(time) {
      if (!time) return "";
      // 直接返回已格式化的时间字符串
      return time;
    },
    getShopMenu(shop) {
      // 直接跳转到商品列表页，使用 query 传递店铺基本信息
      this.$router.push({
        path: "/goods-list",
        query: {
          shopId: shop.shopId,
          customerShopCode: shop.customerShopCode,
          shopName: shop.shopName,
          shopAddress: shop.address,
          shopTel: shop.tel,
          shopLongitude: shop.longitude,
          shopLatitude: shop.latitude,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fff;

  .city-select {
    display: flex;
    align-items: center;
    font-size: 16px;
    padding-right: 12px;
    color: #333;
    white-space: nowrap;

    .van-icon {
      margin-left: 3px;
      font-size: 12px;
    }
  }

  .search-box {
    flex: 1;
    min-width: 0; // 保留这个关键的 flex 修正

    :deep(.van-search) {
      padding: 0;
    }

    :deep(.van-search__content) {
      background: #f7f8fa;
    }
  }
}

.city-select-page {
  background: #fff;
  height: 100vh;
  display: flex;
  flex-direction: column;

  .city-list-component {
    flex: 1;
    overflow-y: auto;
  }

  :deep(.van-cell) {
    text-align: left;
  }

  :deep(.van-index-anchor) {
    background: #fff;
    color: #999;
    padding: 10px 16px;
    font-size: 14px;
    line-height: normal;
    text-align: left;
  }

  :deep(.van-index-bar__index) {
    color: #999;
    padding: 2px 8px;
    font-size: 12px;
  }

  :deep(.van-index-bar__index--active) {
    color: #07c160;
    background-color: transparent;
  }
}

.shop-list {
  padding: 12px;
  // 移除背景色，让 body 的背景色透出来
  // background: #f7f8fa;
  min-height: calc(100vh - 54px);

  .shop-item {
    margin-bottom: 12px;
  }

  .shop-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;

    &:active {
      opacity: 0.8;
    }

    .shop-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .shop-title {
        display: flex;
        align-items: center;
        font-size: 16px;
        font-weight: 500;
        flex: 1;
        min-width: 0; // 保留这个关键的 flex 修正

        .van-icon {
          color: #ffd21e;
          margin-right: 4px;
        }

        .name {
          color: #323233;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .distance {
        color: #969799;
        font-size: 14px;
        margin-left: 8px;
        white-space: nowrap;
      }
    }

    .shop-tags {
      margin-bottom: 8px;

      .tag {
        display: inline-block;
        padding: 2px 6px;
        margin-right: 8px;
        border-radius: 4px;
        font-size: 12px;
      }

      .hall {
        color: #07c160;
        background: #e8f7ef;
      }

      .takeaway {
        color: #1989fa;
        background: #eaf3fe;
      }
    }

    .shop-info {
      font-size: 13px;
      color: #646566;
      text-align: left;

      .address {
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .business-time {
        margin: 0;
      }
    }
  }
}
</style>